import React, { useState } from "react";
import { FaExclamationTriangle, FaCheckCircle, FaTruck, FaTrailer } from "react-icons/fa";
import BaseModalAnimated from "../../components/modal/BaseModalAnimated";
import { useFormik } from "formik";
import * as Yup from "yup";
import CustomInput from "../../components/form/CustomInput";
import CustomButton from "../../components/form/CustomButton";
import CustomSelect from "../../components/form/CustomSelect";
import { showFormErrors } from "../../helpers/showFormErrors";
import { reportResource } from "../../helpers/configRoutes/backend_helper";
import { useSelector } from "react-redux";

const getStopTrailerId = (stop) => stop?.effective_trailer_id || stop?.trailer_id || "";

const ReportModal = ({ modal, setModal, setFilters, filters }) => {
    const [loading, setLoading] = useState(false);
    const reportItm = useSelector((state) => state.ReportsRequest.report);

    const validation = useFormik({
        initialValues: reportItm,
        enableReinitialize: true,
        validationSchema: Yup.object({
            description: Yup.string().required("Description is required"),
            stop_id: Yup.string().required("Please select a stop"),
        }),
        onSubmit: async (values) => {
            setLoading(true);
            const response = await reportResource('post', values);
            if (response.status) {
                setFilters({ ...filters });
                setModal({ show: false });
            }
            setLoading(false);
        },
    });

    const handleStopChange = (event) => {
        const nextStopId = event.target.value;
        const selectedStop = (modal.stops || []).find((stop) => String(stop.id) === String(nextStopId));

        validation.setFieldValue("stop_id", nextStopId);
        validation.setFieldValue("trailer_id", getStopTrailerId(selectedStop));
    };

    return (
        <BaseModalAnimated modal={modal} setModal={setModal}>
            <div className="bg-white rounded-t-[2.5rem] overflow-hidden">
                {/* HEADER MODAL */}
                <div className="bg-cs-danger/10 p-6 flex items-center gap-4 border-b border-cs-danger/10">
                    <div className="w-12 h-12 rounded-2xl bg-cs-danger flex items-center justify-center text-white shadow-lg shadow-cs-danger/20">
                        <FaExclamationTriangle size={20} />
                    </div>
                    <div>
                        <h2 className="text-lg font-bold text-cs-dark tracking-tight">Report Incident</h2>
                        <p className="text-[10px] text-cs-danger uppercase font-bold tracking-widest leading-none">High Priority</p>
                    </div>
                </div>

                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        showFormErrors(validation);
                    }}
                    className="p-6 space-y-6"
                >
                    {/* INPUTS PRINCIPALES */}
                    <div className="grid grid-cols-1 gap-5">
                        <CustomSelect
                            label="Related Stop"
                            options={modal.stops || []}
                            name="stop_id"
                            labelClassName="text-[11px] font-bold text-cs-muted uppercase tracking-wider"
                            error={validation.touched.stop_id && validation.errors.stop_id}
                            value={validation.values.stop_id}
                            handleChange={handleStopChange}
                        />

                        <CustomInput
                            label="Incident Description"
                            placeholder="Briefly describe what happened..."
                            type="text"
                            name="description"
                            labelClassName="text-[11px] font-bold text-cs-muted uppercase tracking-wider"
                            error={validation.touched.description && validation.errors.description}
                            value={validation.values.description}
                            handleChange={validation.handleChange}
                        />
                    </div>

                    {/* SELECTORES DE FALLA (BOTONES TÁCTILES) */}
                    <div className="space-y-3">
                        <p className="text-[11px] font-bold text-cs-muted uppercase tracking-wider ml-1">Asset Failure Involvement</p>
                        <div className="grid grid-cols-2 gap-3">
                            {/* Truck Failure Toggle */}
                            <button
                                type="button"
                                onClick={() => validation.setFieldValue('truck_failure', !validation.values.truck_failure)}
                                className={`flex items-center justify-center gap-3 p-4 rounded-2xl border-2 transition-all ${validation.values.truck_failure
                                    ? 'bg-cs-danger/5 border-cs-danger text-cs-danger shadow-md'
                                    : 'bg-cs-bg-alt border-cs-border text-cs-muted'
                                    }`}
                            >
                                <FaTruck size={18} />
                                <span className="text-xs font-bold uppercase tracking-tight">Truck</span>
                            </button>

                            {/* Trailer Failure Toggle */}
                            <button
                                type="button"
                                onClick={() => validation.setFieldValue('trailer_failure', !validation.values.trailer_failure)}
                                className={`flex items-center justify-center gap-3 p-4 rounded-2xl border-2 transition-all ${validation.values.trailer_failure
                                    ? 'bg-cs-danger/5 border-cs-danger text-cs-danger shadow-md'
                                    : 'bg-cs-bg-alt border-cs-border text-cs-muted'
                                    }`}
                            >
                                <FaTrailer size={18} />
                                <span className="text-xs font-bold uppercase tracking-tight">Trailer</span>
                            </button>
                        </div>
                    </div>

                    {/* ACTION BUTTONS */}
                    <div className="pt-4 flex flex-col gap-3">
                        <CustomButton
                            type="submit"
                            loading={loading}
                            className="w-full py-4 bg-cs-danger text-white font-black rounded-2xl shadow-xl shadow-cs-danger/20 active:scale-95 transition-all flex items-center justify-center gap-2"
                        >
                            <FaCheckCircle /> SUBMIT REPORT
                        </CustomButton>

                        <button
                            type="button"
                            onClick={() => setModal({ show: false })}
                            className="w-full py-3 text-cs-muted text-xs font-bold uppercase tracking-widest active:scale-95 transition-all"
                        >
                            Discard
                        </button>
                    </div>
                </form>
            </div>
        </BaseModalAnimated>
    );
};

export default ReportModal;